import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DataTable,
  EmptyState,
  SkeletonLoader,
  StatusChip,
  type DataTableColumn,
} from '@shared/design-system/components';
import {
  addSupportTicketComment,
  confirmSupportTicketLink,
  getSupportTicket,
  listSupportTickets,
  rejectSupportTicketLink,
  updateSupportTicket,
  type SupportAnalysisResponse,
  type SupportTicketDetail,
  type SupportTicketLink,
  type SupportTicketOut,
  type TicketState,
} from '../../api/supportClient';
import { AnalysisJobView } from '../../components/AnalysisResultView';
import { useAnalysisJob } from '../../components/use-analysis-job';
import { HeaderFilterSelect } from '../../components/HeaderFilter';
import './TicketsPane.css';

/**
 * User-reported tickets (hardening plan Phase 3, 3g), cross-site.
 *
 * This pane exists because the capture widget shipped with no reader on
 * this side at all: tickets are written by the operator-tier API, and
 * this console talks only to the support-role process, which did not
 * mount those routes. A real user filed a report and it was invisible
 * in both UIs. The fix was new `/support/tickets` endpoints plus this
 * pane, not a UI-only change.
 *
 * Separate from Diagnose rather than merged into it: a ticket has its
 * own lifecycle (`new` → `triaging` → `waiting_on_user` → `resolved` →
 * `closed`) that is genuinely not an `Issue`'s three triage states, and
 * `waiting_on_user` has no issue equivalent at all. Mapping one onto
 * the other would be lossy in the direction that matters.
 */

const STATES: TicketState[] = ['new', 'triaging', 'waiting_on_user', 'resolved', 'closed'];

const STATE_LABEL: Record<TicketState, string> = {
  new: 'New',
  triaging: 'Triaging',
  waiting_on_user: 'Waiting on user',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATE_KIND: Record<TicketState, 'error' | 'warning' | 'active' | 'completed' | 'idle'> = {
  new: 'error',
  triaging: 'active',
  waiting_on_user: 'warning',
  resolved: 'completed',
  closed: 'idle',
};

function severityKind(severity: string): 'error' | 'warning' | 'active' {
  if (severity === 'critical') return 'error';
  if (severity === 'warning') return 'warning';
  return 'active';
}

/** The issue this ticket is actually ABOUT, or null.
 *
 *  Only a CONFIRMED link counts. Unconfirmed links are time-proximity
 *  guesses the pane itself refuses to present as fact ("never
 *  auto-linked ... Confirm or reject each one"), and both affordances
 *  keyed off this -- a deep link labelled as this ticket's issue, and
 *  a runbook run whose audit reason names it -- would be asserting the
 *  correlation is real. A candidate is still one click from confirmed.
 */
function confirmedIssueLink(t: SupportTicketDetail): SupportTicketLink | null {
  return t.links.find((l) => l.kind === 'issue' && l.confirmed_at_ms != null) ?? null;
}

/** Route into Pane 3 for an AI candidate raised from a ticket.
 *
 *  `site` is mandatory here in a way it is not on the Diagnose side:
 *  ActPane's confirm form cannot be submitted without one
 *  (`disabled={!site || ...}`), so a `Run →` on a site-less ticket
 *  would land the engineer on a form they cannot send. Callers must
 *  therefore not build this at all when the ticket has no site.
 *
 *  `issue` is carried only when a confirmed link supplies one -- it
 *  feeds ActPane's default reason text, and naming an unconfirmed
 *  correlation in a permanent audit reason is exactly the wrong place
 *  to guess. */
function ticketActHref(site: string, issueId: string | null, runbookId: string): string {
  const qs = new URLSearchParams();
  qs.set('site', site);
  if (issueId) qs.set('issue', issueId);
  qs.set('runbook', runbookId);
  return `/act?${qs.toString()}`;
}

function TicketDetail({ ticketId }: { ticketId: string }) {
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const [visibility, setVisibility] = useState<'internal' | 'requester_visible'>('internal');
  // The whole POST response, not just its id: `cached: true` is
  // reported only by the POST -- `GET /analyses/{id}` cannot know it --
  // and it is what tells the engineer how old the answer they are
  // reading is. Local (not lifted like Diagnose's) because the panel is
  // keyed by ticket and one report's analysis must not follow you to
  // the next.
  const [startedAnalysis, setStartedAnalysis] = useState<SupportAnalysisResponse | null>(null);

  const detail = useQuery({
    queryKey: ['support', 'tickets', ticketId],
    queryFn: () => getSupportTicket(ticketId),
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['support', 'tickets'] });
  }

  const setState = useMutation({
    mutationFn: (state: TicketState) => updateSupportTicket(ticketId, { state }),
    onSuccess: invalidate,
  });
  const postComment = useMutation({
    mutationFn: () => addSupportTicketComment(ticketId, { body: comment, visibility }),
    onSuccess: () => {
      setComment('');
      invalidate();
    },
  });
  const confirmLink = useMutation({
    mutationFn: (linkId: number) => confirmSupportTicketLink(ticketId, linkId),
    onSuccess: invalidate,
  });
  const rejectLink = useMutation({
    mutationFn: (linkId: number) => rejectSupportTicketLink(ticketId, linkId),
    onSuccess: invalidate,
  });

  // Same job, same poll, same rendering as Diagnose -- the endpoint
  // now takes either target shape and builds the prompt from the
  // ticket's own text (folding in a linked issue's machine context
  // when there is one), so the only thing this side owns is WHICH
  // target it names.
  const ai = useAnalysisJob({
    target: { ticket_id: ticketId },
    started: startedAnalysis,
    onStarted: setStartedAnalysis,
  });

  if (detail.isLoading) return <SkeletonLoader shape="text-line" count={5} />;
  if (detail.isError) {
    return <EmptyState title="Could not load ticket" description={String(detail.error)} />;
  }
  const t = detail.data;
  if (!t) return null;

  const snapshotAvailable = t.context_snapshot?.available === true;
  const linkedIssue = confirmedIssueLink(t);
  // `SupportTicketLink` carries no site of its own, so the ticket's
  // site is the only one available here -- there is nothing to fall
  // back to when it is empty.
  const site = t.site ?? '';
  const analysis = ai.job;
  const diagnoseHref = linkedIssue
    ? `/diagnose?${new URLSearchParams(
        site ? { issue: linkedIssue.target_id, site } : { issue: linkedIssue.target_id },
      ).toString()}`
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <h3 style={{ margin: '0 0 4px' }}>{t.title}</h3>
        <div style={{ color: 'var(--nx-muted)', fontSize: 13 }}>
          <code style={{ color: 'var(--nx-cyan)' }}>{t.ticket_id}</code> · {t.site} · reported{' '}
          {new Date(t.created_at_ms).toLocaleString()}
        </div>
        <div style={{ color: 'var(--nx-muted)', fontSize: 12, marginTop: 2 }}>
          by {t.requester_display_name ?? t.requester_sub}
        </div>
        <p className="nx-tickets__body">{t.body}</p>
      </div>

      <section>
        <h4 style={{ marginBottom: 6 }}>State</h4>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusChip status={STATE_KIND[t.state]} label={STATE_LABEL[t.state]} />
          {STATES.filter((s) => s !== t.state).map((s) => (
            <button key={s} type="button" disabled={setState.isPending} onClick={() => setState.mutate(s)}>
              {STATE_LABEL[s]}
            </button>
          ))}
        </div>
        {setState.isError && (
          <div style={{ color: 'var(--nx-error)', fontSize: 12 }}>{String(setState.error)}</div>
        )}
      </section>

      <section>
        <h4 style={{ marginBottom: 6 }}>AI recovery triage</h4>
        <p style={{ fontSize: 11, color: 'var(--nx-muted)', margin: '0 0 8px' }}>
          The same analysis Diagnose runs, over what the user wrote — plus the machine context
          of a confirmed issue link when there is one. A report is a human account of a
          symptom, so read the answer as a lead rather than a diagnosis.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => ai.start()} disabled={ai.starting}>
            {ai.starting ? 'Starting…' : 'Analyze with AI'}
          </button>
          {/* A confirmed issue link means the incident already exists
              on the Diagnose side with logs, site health and knowledge
              behind it. This is the one hop to it. */}
          {diagnoseHref && (
            <Link
              to={diagnoseHref}
              data-testid="ticket-open-in-diagnose"
              title={`Open issue ${linkedIssue?.target_id} in Diagnose`}
              style={{
                fontSize: 12,
                padding: '3px 10px',
                borderRadius: 'var(--nx-r)',
                border: '1px solid var(--nx-border)',
                background: 'var(--nx-bg3)',
                color: 'var(--nx-cyan)',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Open in Diagnose →
            </Link>
          )}
        </div>
        {ai.startError != null && (
          <div style={{ color: 'var(--nx-error)', fontSize: 12 }}>{String(ai.startError)}</div>
        )}
        {ai.pollError != null && (
          <div
            style={{ color: 'var(--nx-error)', fontSize: 12 }}
            data-testid="analysis-poll-error"
          >
            {String(ai.pollError)}
          </div>
        )}
        {analysis && (
          <AnalysisJobView
            job={analysis}
            runHref={
              site ? (runbookId) => ticketActHref(site, linkedIssue?.target_id ?? null, runbookId) : undefined
            }
            noRunNote="No site is attached to this report, so there is nothing to run a runbook against — attach one, or work the linked issue instead."
            onReanalyze={() => ai.start({ force: true })}
            reanalyzeDisabled={ai.busy}
          />
        )}
      </section>

      <section>
        <h4 style={{ marginBottom: 6 }}>Correlated issues</h4>
        <p style={{ fontSize: 11, color: 'var(--nx-muted)', margin: '0 0 8px' }}>
          Ranked by time proximity to the report, never auto-linked — a wrong auto-link buries a
          real report under an unrelated incident. Confirm or reject each one.
        </p>
        {t.links.length === 0 ? (
          <p style={{ color: 'var(--nx-muted)' }}>No candidates were found near this report.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {t.links.map((l) => (
              <li
                key={l.link_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  padding: '6px 8px',
                  border: '1px solid var(--nx-border)',
                  borderRadius: 'var(--nx-r)',
                }}
              >
                <StatusChip
                  status={l.confirmed_at_ms ? 'completed' : 'warning'}
                  label={l.confirmed_at_ms ? 'confirmed' : 'candidate'}
                />
                <span style={{ flex: 1, minWidth: 200 }}>
                  {l.title ?? <em style={{ color: 'var(--nx-muted)' }}>(title unavailable)</em>}
                  <span style={{ color: 'var(--nx-muted)', fontSize: 11 }}>
                    {' '}
                    · {l.kind} · confidence {l.confidence}
                  </span>
                </span>
                {!l.confirmed_at_ms && (
                  <>
                    <button type="button" onClick={() => confirmLink.mutate(l.link_id)}>
                      Confirm
                    </button>
                    <button type="button" onClick={() => rejectLink.mutate(l.link_id)}>
                      Reject
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h4 style={{ marginBottom: 6 }}>Discussion</h4>
        {t.comments.length === 0 ? (
          <p style={{ color: 'var(--nx-muted)' }}>No comments yet.</p>
        ) : (
          <ul style={{ margin: '0 0 10px', paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {t.comments.map((c) => (
              <li key={c.id} style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--nx-muted)', fontSize: 11 }}>
                  {c.author_display_name ?? c.author_sub} ·{' '}
                  {new Date(c.created_at_ms).toLocaleString()} ·{' '}
                  {c.visibility === 'internal' ? 'internal' : 'visible to reporter'}
                </div>
                <div className="nx-tickets__comment-body">{c.body}</div>
              </li>
            ))}
          </ul>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment"
            rows={2}
            style={{ flex: 1, minWidth: 200, padding: 6 }}
          />
          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as 'internal' | 'requester_visible')}
            aria-label="Comment visibility"
          >
            <option value="internal">Internal</option>
            <option value="requester_visible">Visible to reporter</option>
          </select>
          <button
            type="button"
            disabled={!comment.trim() || postComment.isPending}
            onClick={() => postComment.mutate()}
          >
            Post
          </button>
        </div>
      </section>

      <section>
        <h4 style={{ marginBottom: 6 }}>Context at submit time</h4>
        <p style={{ fontSize: 11, color: 'var(--nx-muted)', margin: '0 0 6px' }}>
          Captured server-side when the report was filed, so it reflects the moment being described
          rather than now.
        </p>
        {!snapshotAvailable ? (
          <p style={{ color: 'var(--nx-muted)' }}>
            No snapshot was captured (the site&rsquo;s worker was unreachable at submit time).
          </p>
        ) : (
          <pre className="nx-tickets__snapshot">
            {JSON.stringify(t.context_snapshot, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}

export function TicketsPane() {
  // `?site=` carries the site the engineer was already looking at
  // (Diagnose writes it) so it survives the trip here. Read on mount
  // only: from then on the dropdown owns it.
  const [params] = useSearchParams();
  const [siteFilter, setSiteFilter] = useState(params.get('site') ?? '');
  const [stateFilter, setStateFilter] = useState<TicketState | ''>('');
  const [selected, setSelected] = useState<string | null>(null);

  const ticketsQuery = useQuery({
    queryKey: ['support', 'tickets'],
    queryFn: () => listSupportTickets({}),
    refetchInterval: 30_000,
  });

  // Memoised so the `?? []` fallback is a stable reference — otherwise
  // it is a fresh array every render and the two useMemo hooks below
  // recompute (and eslint react-hooks/exhaustive-deps flags it).
  const ticketsData = ticketsQuery.data;
  const all = useMemo(() => ticketsData?.tickets ?? [], [ticketsData]);

  const siteOptions = useMemo(() => {
    const sites = new Set(all.map((t) => t.site));
    return [...sites].sort().map((s) => ({ value: s, label: s }));
  }, [all]);

  const rows = useMemo(
    () =>
      all.filter(
        (t) => (!siteFilter || t.site === siteFilter) && (!stateFilter || t.state === stateFilter),
      ),
    [all, siteFilter, stateFilter],
  );

  const columns: DataTableColumn<SupportTicketOut>[] = [
    {
      id: 'ticket_id',
      header: 'Reference',
      cell: (t) => <code style={{ fontSize: 12, color: 'var(--nx-cyan)' }}>{t.ticket_id}</code>,
    },
    { id: 'title', header: 'Report', cell: (t) => t.title, sortable: true, sortValue: (t) => t.title },
    {
      id: 'site',
      header: (
        <HeaderFilterSelect
          label="Site"
          value={siteFilter}
          options={siteOptions}
          allLabel="All sites"
          onChange={setSiteFilter}
        />
      ),
      cell: (t) => t.site,
    },
    {
      id: 'state',
      header: (
        <HeaderFilterSelect
          label="State"
          value={stateFilter}
          options={STATES.map((s) => ({ value: s, label: STATE_LABEL[s] }))}
          allLabel="All states"
          onChange={(v) => setStateFilter(v as TicketState | '')}
        />
      ),
      cell: (t) => <StatusChip status={STATE_KIND[t.state]} label={STATE_LABEL[t.state]} />,
    },
    {
      id: 'severity',
      header: 'Severity',
      cell: (t) => <StatusChip status={severityKind(t.severity)} label={t.severity} />,
    },
    {
      id: 'candidates',
      header: 'Candidates',
      // Surfaced in the list so a queue of tickets with unreviewed
      // correlations is visible without opening each one.
      cell: (t) =>
        t.unconfirmed_link_count > 0 ? (
          <span title="Auto-suggested correlations awaiting review">
            {t.unconfirmed_link_count} to review
          </span>
        ) : (
          <span style={{ color: 'var(--nx-muted)' }}>—</span>
        ),
      align: 'right',
      sortable: true,
      sortValue: (t) => t.unconfirmed_link_count,
    },
    {
      id: 'created',
      header: 'Reported',
      cell: (t) => new Date(t.created_at_ms).toLocaleString(),
      sortable: true,
      sortValue: (t) => t.created_at_ms,
    },
    {
      id: 'reporter',
      header: 'Reported by',
      cell: (t) => (
        <span style={{ fontSize: 12 }}>{t.requester_display_name ?? t.requester_sub}</span>
      ),
    },
  ];

  return (
    <div className="nx-tickets__layout">
      <div className="nx-tickets__col">
        <h2 style={{ marginTop: 0 }}>Tickets</h2>
        <p style={{ color: 'var(--nx-muted)', fontSize: 13, marginTop: 0 }}>
          Problems users reported by hand, from every site. These are distinct from detected issues
          — nothing raised them automatically, so nobody sees them unless someone looks here.
        </p>
        {ticketsQuery.isLoading ? (
          <SkeletonLoader shape="table-row" count={5} />
        ) : ticketsQuery.isError ? (
          <EmptyState title="Could not load tickets" description={String(ticketsQuery.error)} />
        ) : (
          <DataTable
            ariaLabel="Tickets"
            columns={columns}
            rows={rows}
            getRowId={(t) => t.ticket_id}
            onRowClick={(t) => setSelected(t.ticket_id)}
            emptyState={
              <EmptyState
                title="No tickets"
                description="No user-reported tickets match this filter."
              />
            }
          />
        )}
      </div>
      <div className="nx-tickets__col">
        {selected ? (
          // Keyed on the ticket so switching selection REMOUNTS the
          // detail. Without it a running analysis, its result and a
          // half-typed comment would all carry over and read as
          // belonging to the newly selected report.
          <TicketDetail key={selected} ticketId={selected} />
        ) : (
          <EmptyState
            title="Select a ticket"
            description="Pick a report on the left to triage it."
          />
        )}
      </div>
    </div>
  );
}
